import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Table, Tag } from 'antd';
import AlertBox from '@/components/alert-box';

// A fresh `[]` default on every render would re-run the selection effect
// below forever, so the empty case shares one array.
const NO_RECEIPTS = [];
const NO_FILES = [];

const formatAmount = (value) =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

/**
 * Receipts read from the attachments, for review before they join the order.
 *
 * Every row starts ticked except those already on the order (same number and
 * amount), so reading a file twice does not book a taxi twice. The kept rows
 * are added as ordinary receipts and can be corrected afterwards like any
 * typed one — including the company card box, which a receipt cannot tell.
 */
export default function TravelReceiptsReviewModal({
  open,
  receipts = NO_RECEIPTS,
  failedFiles = NO_FILES,
  isPending,
  onAdd,
  onClose,
}) {
  const { t } = useTranslation(['officialTravel']);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const rows = useMemo(
    () => receipts.map((receipt, index) => ({ ...receipt, key: index })),
    [receipts]
  );

  useEffect(() => {
    setSelectedKeys(rows.filter((row) => !row.isDuplicate).map((row) => row.key));
  }, [rows]);

  const columns = [
    {
      title: t('invoices.travelInvoiceNumber', { ns: 'officialTravel' }),
      dataIndex: 'travelInvoiceNumber',
      key: 'travelInvoiceNumber',
      render: (value, row) => (
        <span className="flex flex-wrap items-center gap-1">
          {value || '-'}
          {row.isDuplicate && (
            <Tag color="orange" className="m-0">
              {t('invoices.read.duplicate', { ns: 'officialTravel' })}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: t('invoices.travelInvoiceDate', { ns: 'officialTravel' }),
      dataIndex: 'travelInvoiceDate',
      key: 'travelInvoiceDate',
      render: (value) => value || '-',
    },
    {
      title: t('invoices.travelInvoiceDescription', { ns: 'officialTravel' }),
      dataIndex: 'travelInvoiceDescription',
      key: 'travelInvoiceDescription',
    },
    {
      title: t('invoices.travelInvoiceType', { ns: 'officialTravel' }),
      dataIndex: 'travelInvoiceType',
      key: 'travelInvoiceType',
      render: (value, row) =>
        [
          value && t(`options.invoiceType.${value}`, { ns: 'officialTravel' }),
          row.travelInvoiceCostKind &&
            t(`options.costKind.${row.travelInvoiceCostKind}`, {
              ns: 'officialTravel',
            }),
        ]
          .filter(Boolean)
          .join(' · '),
    },
    {
      title: t('invoices.travelInvoiceAmount', { ns: 'officialTravel' }),
      key: 'amount',
      align: 'right',
      render: (_, row) =>
        `${formatAmount(row.travelInvoiceAmount)} ${row.travelInvoiceCurrency}`,
    },
    {
      title: t('invoices.read.file', { ns: 'officialTravel' }),
      dataIndex: 'sourceFile',
      key: 'sourceFile',
      ellipsis: true,
    },
  ];

  const handleAdd = () => {
    const kept = rows
      .filter((row) => selectedKeys.includes(row.key))
      .map((row) => ({
        travelInvoiceNumber: row.travelInvoiceNumber,
        travelInvoiceDate: row.travelInvoiceDate,
        travelInvoiceDescription: row.travelInvoiceDescription,
        travelInvoiceType: row.travelInvoiceType,
        travelInvoiceCostKind: row.travelInvoiceCostKind || '',
        travelInvoiceAmount: Number(row.travelInvoiceAmount) || 0,
        travelInvoiceCurrency: row.travelInvoiceCurrency,
        isTravelInvoicePaidByCard: false,
      }));

    onAdd(kept);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={960}
      // `.btn-primary` is a flex box globally, which breaks the footer's
      // inline row; laying the footer out as flex keeps both buttons on it.
      classNames={{ footer: 'flex justify-end gap-2' }}
      title={t('invoices.read.title', { ns: 'officialTravel' })}
      footer={[
        <Button key="cancel" shape="round" onClick={onClose}>
          {t('actions.cancel', { ns: 'officialTravel' })}
        </Button>,
        <Button
          key="add"
          type="primary"
          shape="round"
          className="btn-primary"
          disabled={!selectedKeys.length || isPending}
          loading={isPending}
          onClick={handleAdd}
        >
          {t('invoices.read.add', {
            ns: 'officialTravel',
            count: selectedKeys.length,
          })}
        </Button>,
      ]}
    >
      <div className="grid gap-3">
        <AlertBox variant="info" className="mb-0">
          <p>{t('invoices.read.hint', { ns: 'officialTravel' })}</p>
        </AlertBox>

        {failedFiles.length > 0 && (
          <AlertBox variant="warning" className="mb-0">
            <p>
              {t('invoices.read.failedFiles', { ns: 'officialTravel' })}{' '}
              {failedFiles.join(', ')}
            </p>
          </AlertBox>
        )}

        {rows.length === 0 ? (
          <p className="text-sm text-zinc-600">
            {t('invoices.read.empty', { ns: 'officialTravel' })}
          </p>
        ) : (
          <Table
            dataSource={rows}
            columns={columns}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: true }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: setSelectedKeys,
            }}
          />
        )}
      </div>
    </Modal>
  );
}
